// Generator för de engelska fotosidorna (/en/photography/*).
// Speglar fotoTopicRoute men med engelsk copy, hreflang mot SV-motsvarigheten
// och kontakt mot /en/contact i stället för det svenska offertformuläret.

import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { coverPhotoFor, galleryPhotos, photosBy } from "@/content/photo-gallery";
import { PHOTOGRAPHER } from "@/content/foto";
import { enPhotoPages } from "@/content/photography-en";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  person,
  serviceSchema,
  speakable,
} from "@/lib/seo/schema";

export function enPhotoRoute(path: string) {
  const page = enPhotoPages.find((p) => p.path === path);
  if (!page) throw new Error(`Unknown EN photo page ${path}`);
  const URL = `https://fenny.se${page.path}`;
  const SV_URL = `https://fenny.se${page.svPath}`;
  const gallery = page.gallery === "alla" ? galleryPhotos : photosBy(page.gallery);
  const photos = (gallery.length >= 9 ? gallery : galleryPhotos).slice(0, 14);
  const cover = coverPhotoFor(page.path, page.gallery === "alla" ? undefined : page.gallery);
  const FAQ: LandingFAQ[] = page.faq.map((f) => ({ q: f.q, a: f.a }));

  return {
    head: () => ({
      meta: [
        { title: page.metaTitle },
        { name: "description", content: page.description },
        { property: "og:title", content: page.metaTitle },
        { property: "og:description", content: page.description },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: URL },
      ],
      links: [
        { rel: "canonical", href: URL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            aeoGeo({
              path: page.path,
              name: page.metaTitle,
              description: page.description,
              question: page.question,
              answer: page.quickAnswer,
              speakableSelectors: ["[data-quick-answer]"],
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: page.crumb,
              path: page.path,
              serviceType: page.serviceType,
              description: page.description,
              areaServed: ["Sweden", "Stockholm", "Gothenburg", "Malmö"],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            imageGallery({
              name: `${page.crumb} — Fenny Photography`,
              path: page.path,
              description: page.galleryHeading,
              images: photos,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            person({
              name: PHOTOGRAPHER.name,
              jobTitle: "Photographer",
              email: "hey@fenny.se",
              path: page.path,
              description:
                "Fenny shoots corporate portraits, campaign and event photography for Swedish and international companies operating in Sweden.",
              knowsAbout: page.knowsAbout,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs(
              page.slug
                ? [
                    { name: "Home", path: "/en" },
                    { name: "Photography", path: "/en/photography" },
                    { name: page.crumb, path: page.path },
                  ]
                : [
                    { name: "Home", path: "/en" },
                    { name: "Photography", path: page.path },
                  ],
            ),
          ),
        },
      ],
    }),
    component: function EnPhotoPage() {
      return (
        <LandingPage
          heroImage={{ src: cover.src, alt: `${page.eyebrow} — photography by Fenny` }}
          eyebrow={page.eyebrow}
          title={page.h1}
          lede={page.lede}
          tldr={<p>{page.quickAnswer}</p>}
          ctaPrimary={{ label: "Request a quote", to: "/en/contact" }}
          ctaSecondary={{ label: "See the portfolio", to: "/en/photography" }}
          trackingId={`en_photo_${page.slug || "hub"}`}
          stills={false}
          videos={false}
          afterHero={
            <div className="py-12">
              <PhotoMasonry
                photos={photos}
                filterable={false}
                initial={9}
                eyebrow="Portfolio"
                heading={page.galleryHeading}
              />
            </div>
          }
          bullets={page.bullets}
          {...(page.pricing
            ? {
                pricing: {
                  heading: "Pricing 2026",
                  intro: page.pricing.intro,
                  rows: page.pricing.rows,
                  footnote: page.pricing.footnote,
                },
              }
            : {})}
          {...(page.process
            ? { process: { heading: "How it works", steps: page.process } }
            : {})}
          sections={[
            ...page.sections.map((s) => ({
              id: s.id,
              heading: s.heading,
              body: (
                <>
                  {s.paragraphs.map((p, i) => (
                    <p key={i} className={i > 0 ? "mt-4" : undefined}>
                      {p}
                    </p>
                  ))}
                </>
              ),
            })),
            {
              id: "swedish-version",
              heading: "På svenska",
              body: (
                <p>
                  This page is also available in Swedish:{" "}
                  <a
                    className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                    href={page.svPath}
                  >
                    {page.svPath}
                  </a>
                  .
                </p>
              ),
            },
            {
              id: "read-more",
              heading: "Read more",
              body: (
                <p>
                  {page.related.map((r, i) => (
                    <span key={r.to}>
                      {i > 0 ? " · " : ""}
                      <a
                        className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                        href={r.to}
                      >
                        {r.label}
                      </a>
                    </span>
                  ))}
                </p>
              ),
            },
          ]}
          faq={FAQ}
          riskReducers
          closing={{
            title: "Shall we price your assignment?",
            body: "Describe what you need and Fenny comes back with scope and a fixed price within one working day.",
            primary: { label: "Request a quote", to: "/en/contact" },
            secondary: { label: "Contact us", to: "/en/contact" },
          }}
        />
      );
    },
  };
}
